/**
 * Carrier MCP — Credits System v2.0 (Pricing Evolution)
 *
 * Implements credit-based billing inspired by Stripe's Pricing Model Evolution Guide:
 *   - Monthly credit allotments per tier (Lovable pattern)
 *   - Auto-billed overages when base credits exhausted (Warp pattern)
 *   - Credit rollovers for irregular usage (Lovable pattern)
 *   - Daily free credits for acquisition (Browserbase/Lovable pattern)
 *   - Billing thresholds to prevent bill shock (Hex pattern)
 *   - Volume discounts for high-usage customers
 *
 * Storage layout (CARRIER_USERS KV):
 *   key: credits:<sub>:<yyyymm>     value: CreditLedger
 *   key: credits:daily:<sub>:<yyyymmdd>  value: DailyFreeCredits
 *   key: credits:config:<sub>       value: CreditConfig (overrides, thresholds)
 *   key: credits:rollover:<sub>     value: RolloverBalance
 *
 * Credit economy:
 *   1 tool call = 1 credit (read scope)
 *   1 tool call = 2 credits (write scope)
 *   1 tool call = 5 credits (admin scope)
 *   Intelligence composites = 3 credits each
 *
 * Stripe integration:
 *   - Overages reported via Stripe Metered Billing (usage_records API)
 *   - Billing thresholds trigger Stripe invoice finalization
 *   - Credit grants created via Stripe Customer Balance Transactions
 */

import type { Env } from "./types.js";
import type { Tier, ScopeToken } from "./billing.js";

// ---------------------------------------------------------------------------
// Credit Constants
// ---------------------------------------------------------------------------

/** Monthly credit allotments per tier */
export const TIER_CREDIT_ALLOTMENTS: Record<Tier, number> = {
  free: 5_000,
  pro: 100_000,
  enterprise: Infinity,
  superadmin: Infinity,
};

/** Daily free credits for all users (acquisition driver) */
export const DAILY_FREE_CREDITS = 50;

/** Maximum rollover credits (cap at 2x monthly allotment) */
export const ROLLOVER_CAP_MULTIPLIER = 2;

/** Credit cost per scope */
export const SCOPE_CREDIT_COSTS: Record<ScopeToken | "intelligence", number> = {
  read: 1,
  write: 2,
  admin: 5,
  intelligence: 3,
};

/** Volume discount tiers (cumulative monthly usage) */
export const VOLUME_DISCOUNTS: VolumeDiscountTier[] = [
  { threshold: 50_000, discountPct: 0 },
  { threshold: 100_000, discountPct: 10 },
  { threshold: 250_000, discountPct: 15 },
  { threshold: 500_000, discountPct: 20 },
  { threshold: 1_000_000, discountPct: 25 },
];

/** Default billing threshold (in cents) — triggers invoice at this spend level */
export const DEFAULT_BILLING_THRESHOLD_CENTS = 10_000; // $100

/** Overage price per credit (in cents) — Pro tier */
export const OVERAGE_PRICE_PER_CREDIT_CENTS = 0.1; // $0.001 per credit

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface VolumeDiscountTier {
  threshold: number;
  discountPct: number;
}

export interface CreditLedger {
  /** Monthly allotment (base + rollover) */
  allotment: number;
  /** Credits consumed this period */
  consumed: number;
  /** Credits from rollovers applied this month */
  rollover_applied: number;
  /** Overage credits consumed beyond allotment */
  overage: number;
  /** Overage amount billed (cents) */
  overage_billed_cents: number;
  /** Whether billing threshold was hit this period */
  threshold_triggered: boolean;
  /** ISO 8601 last updated */
  updated_at: string;
}

export interface DailyFreeCredits {
  /** Credits granted today */
  granted: number;
  /** Credits consumed from daily free pool */
  consumed: number;
  /** ISO date (YYYY-MM-DD) */
  date: string;
}

export interface CreditConfig {
  /** Custom billing threshold override (cents) */
  billing_threshold_cents: number;
  /** Whether overages are enabled (Pro+) */
  overages_enabled: boolean;
  /** Whether rollovers are enabled (Pro+) */
  rollovers_enabled: boolean;
  /** Custom overage rate override (cents per credit) */
  overage_rate_cents: number;
  /** Volume discount tier override */
  volume_discount_pct: number;
  /** Notification preferences */
  notify_at_pct: number[]; // e.g., [50, 80, 95, 100]
  updated_at: string;
}

export interface RolloverBalance {
  /** Unused credits from previous month eligible for rollover */
  credits: number;
  /** Source month (YYYYMM) */
  source_month: string;
  /** Expiry — rollovers expire after 1 month */
  expires_at: string;
}

export interface CreditCheckResult {
  allowed: boolean;
  credits_remaining: number;
  daily_free_remaining: number;
  overage_active: boolean;
  overage_amount_cents: number;
  threshold_pct: number;
  tier: Tier;
  volume_discount_pct: number;
  reset_at: string;
}

export interface CreditDeductionResult {
  success: boolean;
  credits_deducted: number;
  source: "allotment" | "daily_free" | "overage";
  new_balance: number;
  overage_triggered: boolean;
  threshold_triggered: boolean;
}

// ---------------------------------------------------------------------------
// Credit Check
// ---------------------------------------------------------------------------

/**
 * Check if a user has sufficient credits for a tool call.
 * Order of credit consumption:
 *   1. Daily free credits (if available)
 *   2. Monthly allotment (base + rollover)
 *   3. Overage (if enabled for tier)
 */
export async function checkCredits(
  env: Env,
  sub: string,
  tier: Tier,
  scope: ScopeToken | "intelligence",
): Promise<CreditCheckResult> {
  const cost = SCOPE_CREDIT_COSTS[scope];
  const month = currentMonth();
  const today = currentDay();
  const resetAt = firstDayNextMonth();

  // Enterprise always allowed
  if (tier === "enterprise" || tier === "superadmin") {
    return {
      allowed: true,
      credits_remaining: Infinity,
      daily_free_remaining: Infinity,
      overage_active: false,
      overage_amount_cents: 0,
      threshold_pct: 0,
      tier,
      volume_discount_pct: 0,
      reset_at: resetAt,
    };
  }

  // Load ledger
  const ledger = await getLedger(env, sub, month, tier);
  const dailyFree = await getDailyFreeCredits(env, sub, today);
  const config = await getCreditConfig(env, sub, tier);

  // Calculate remaining
  const monthlyRemaining = Math.max(0, ledger.allotment - ledger.consumed);
  const dailyFreeRemaining = Math.max(0, dailyFree.granted - dailyFree.consumed);
  const totalRemaining = dailyFreeRemaining + monthlyRemaining;

  // Check if allowed
  const overageEnabled = config.overages_enabled && tier !== "free";
  const allowed = totalRemaining >= cost || overageEnabled;

  // Volume discount
  const volumeDiscount = resolveVolumeDiscount(ledger.consumed);

  // Threshold percentage
  const thresholdPct = config.billing_threshold_cents > 0
    ? Math.round((ledger.overage_billed_cents / config.billing_threshold_cents) * 100)
    : 0;

  return {
    allowed,
    credits_remaining: monthlyRemaining,
    daily_free_remaining: dailyFreeRemaining,
    overage_active: monthlyRemaining < cost && dailyFreeRemaining < cost && overageEnabled,
    overage_amount_cents: ledger.overage_billed_cents,
    threshold_pct: thresholdPct,
    tier,
    volume_discount_pct: volumeDiscount,
    reset_at: resetAt,
  };
}

// ---------------------------------------------------------------------------
// Credit Deduction
// ---------------------------------------------------------------------------

/**
 * Deduct credits for a tool call. Fire-and-forget in the hot path.
 *
 * Consumption order:
 *   1. Daily free credits first (lowest cost to user)
 *   2. Monthly allotment
 *   3. Overage (auto-billed, Pro+ only)
 */
export function deductCredits(
  env: Env,
  sub: string,
  tier: Tier,
  scope: ScopeToken | "intelligence",
): void {
  void (async () => {
    const cost = SCOPE_CREDIT_COSTS[scope];
    const month = currentMonth();
    const today = currentDay();

    // Enterprise — no deduction needed
    if (tier === "enterprise" || tier === "superadmin") return;

    // Try daily free credits first
    const dailyFree = await getDailyFreeCredits(env, sub, today);
    const dailyRemaining = dailyFree.granted - dailyFree.consumed;

    if (dailyRemaining >= cost) {
      // Deduct from daily free pool
      await putDailyFreeCredits(env, sub, today, {
        ...dailyFree,
        consumed: dailyFree.consumed + cost,
      });
      return;
    }

    // Deduct from monthly allotment
    const ledger = await getLedger(env, sub, month, tier);
    const monthlyRemaining = ledger.allotment - ledger.consumed;

    if (monthlyRemaining >= cost) {
      await putLedger(env, sub, month, {
        ...ledger,
        consumed: ledger.consumed + cost,
        updated_at: new Date().toISOString(),
      });
      return;
    }

    // Overage path (Pro+ only)
    const config = await getCreditConfig(env, sub, tier);
    if (!config.overages_enabled || tier === "free") return;

    const volumeDiscount = resolveVolumeDiscount(ledger.consumed);
    const effectiveRate = OVERAGE_PRICE_PER_CREDIT_CENTS * (1 - volumeDiscount / 100);
    const overageCostCents = Math.round(cost * effectiveRate * 100) / 100;

    const updatedLedger: CreditLedger = {
      ...ledger,
      consumed: ledger.consumed + cost,
      overage: ledger.overage + cost,
      overage_billed_cents: ledger.overage_billed_cents + overageCostCents,
      updated_at: new Date().toISOString(),
    };

    // Check billing threshold
    if (
      !ledger.threshold_triggered &&
      updatedLedger.overage_billed_cents >= config.billing_threshold_cents
    ) {
      updatedLedger.threshold_triggered = true;
      // Trigger Stripe invoice finalization (async, non-blocking)
      void triggerBillingThreshold(env, sub, updatedLedger.overage_billed_cents);
    }

    await putLedger(env, sub, month, updatedLedger);

    // Push overage to Stripe at every 100-credit boundary
    if (updatedLedger.overage % 100 === 0) {
      void pushOverageToStripe(env, sub, 100, effectiveRate).catch(() => {});
    }
  })();
}

// ---------------------------------------------------------------------------
// Credit Grant (Rollover)
// ---------------------------------------------------------------------------

/**
 * Calculate and apply rollover credits from previous month.
 * Called by the monthly cron job (scheduled handler).
 *
 * Rules:
 *   - Only Pro+ tiers get rollovers
 *   - Rollover = min(unused credits, allotment * ROLLOVER_CAP_MULTIPLIER)
 *   - Rollovers expire after 1 month (use-it-or-lose-it next cycle)
 */
export async function applyMonthlyRollover(
  env: Env,
  sub: string,
  tier: Tier,
): Promise<RolloverBalance | null> {
  if (tier === "free") return null;

  const prevMonth = previousMonth();
  const prevLedger = await getLedger(env, sub, prevMonth, tier);

  const unused = Math.max(0, prevLedger.allotment - prevLedger.consumed);
  if (unused === 0) return null;

  const maxRollover = TIER_CREDIT_ALLOTMENTS[tier] * ROLLOVER_CAP_MULTIPLIER;
  const rolloverAmount = Math.min(unused, maxRollover);

  const rollover: RolloverBalance = {
    credits: rolloverAmount,
    source_month: prevMonth,
    expires_at: firstDayMonthAfterNext(),
  };

  await env.CARRIER_USERS.put(
    `credits:rollover:${sub}`,
    JSON.stringify(rollover),
    { expirationTtl: 62 * 24 * 60 * 60 }, // ~2 months
  );

  // Apply to current month's ledger
  const currentMo = currentMonth();
  const currentLedger = await getLedger(env, sub, currentMo, tier);
  await putLedger(env, sub, currentMo, {
    ...currentLedger,
    allotment: currentLedger.allotment + rolloverAmount,
    rollover_applied: rolloverAmount,
    updated_at: new Date().toISOString(),
  });

  return rollover;
}

// ---------------------------------------------------------------------------
// Volume Discounts
// ---------------------------------------------------------------------------

/**
 * Resolve the applicable volume discount percentage based on cumulative usage.
 */
export function resolveVolumeDiscount(consumed: number): number {
  let discount = 0;
  for (const tier of VOLUME_DISCOUNTS) {
    if (consumed >= tier.threshold) {
      discount = tier.discountPct;
    } else {
      break;
    }
  }
  return discount;
}

// ---------------------------------------------------------------------------
// Billing Threshold
// ---------------------------------------------------------------------------

/**
 * Trigger billing threshold — creates a Stripe invoice for accumulated overages.
 * Prevents bill shock by invoicing incrementally (Hex pattern).
 */
async function triggerBillingThreshold(
  env: Env,
  sub: string,
  amountCents: number,
): Promise<void> {
  const stripeKey = (env as Env & { STRIPE_SECRET_KEY?: string }).STRIPE_SECRET_KEY;
  if (!stripeKey) return;

  const customerId = await env.CARRIER_USERS.get(`stripe_customer_id:${sub}`);
  if (!customerId) return;

  // Create an invoice item for the threshold amount
  const body = new URLSearchParams({
    customer: customerId,
    amount: String(Math.round(amountCents)),
    currency: "usd",
    description: `Carrier MCP overage credits (threshold reached)`,
  });

  const resp = await fetch("https://api.stripe.com/v1/invoiceitems", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${stripeKey}`,
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body: body.toString(),
  });

  if (!resp.ok) return;

  // Create and finalize the invoice
  const invoiceBody = new URLSearchParams({
    customer: customerId,
    auto_advance: "true", // Auto-finalize and attempt payment
    "collection_method": "charge_automatically",
    description: "Carrier MCP — Overage billing threshold invoice",
  });

  await fetch("https://api.stripe.com/v1/invoices", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${stripeKey}`,
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body: invoiceBody.toString(),
  });
}

/**
 * Push overage usage records to Stripe Metered Billing.
 */
async function pushOverageToStripe(
  env: Env,
  sub: string,
  quantity: number,
  ratePerCredit: number,
): Promise<void> {
  const stripeKey = (env as Env & { STRIPE_SECRET_KEY?: string }).STRIPE_SECRET_KEY;
  if (!stripeKey) return;

  const subItemId = await env.CARRIER_USERS.get(`stripe_sub_item_id:${sub}`);
  if (!subItemId) return;

  const body = new URLSearchParams({
    quantity: String(quantity),
    timestamp: String(Math.floor(Date.now() / 1000)),
    action: "increment",
  });

  await fetch(
    `https://api.stripe.com/v1/subscription_items/${subItemId}/usage_records`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${stripeKey}`,
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: body.toString(),
    },
  );

  // Store rate for reconciliation
  await env.CARRIER_USERS.put(
    `overage_rate:${sub}`,
    JSON.stringify({ rate_cents: ratePerCredit, updated_at: new Date().toISOString() }),
    { expirationTtl: 35 * 24 * 60 * 60 },
  );
}

// ---------------------------------------------------------------------------
// KV Helpers
// ---------------------------------------------------------------------------

async function getLedger(
  env: Env,
  sub: string,
  month: string,
  tier: Tier,
): Promise<CreditLedger> {
  const key = `credits:${sub}:${month}`;
  const raw = await env.CARRIER_USERS.get(key, "json").catch(() => null);
  if (raw && typeof raw === "object" && "allotment" in raw) {
    return raw as CreditLedger;
  }
  // Initialize new ledger for the month
  const allotment = TIER_CREDIT_ALLOTMENTS[tier];
  return {
    allotment,
    consumed: 0,
    rollover_applied: 0,
    overage: 0,
    overage_billed_cents: 0,
    threshold_triggered: false,
    updated_at: new Date().toISOString(),
  };
}

async function putLedger(
  env: Env,
  sub: string,
  month: string,
  ledger: CreditLedger,
): Promise<void> {
  const key = `credits:${sub}:${month}`;
  await env.CARRIER_USERS.put(key, JSON.stringify(ledger), {
    expirationTtl: 65 * 24 * 60 * 60, // ~2 months
  });
}

async function getDailyFreeCredits(
  env: Env,
  sub: string,
  day: string,
): Promise<DailyFreeCredits> {
  const key = `credits:daily:${sub}:${day}`;
  const raw = await env.CARRIER_USERS.get(key, "json").catch(() => null);
  if (raw && typeof raw === "object" && "granted" in raw) {
    return raw as DailyFreeCredits;
  }
  // Initialize daily free credits
  return {
    granted: DAILY_FREE_CREDITS,
    consumed: 0,
    date: day,
  };
}

async function putDailyFreeCredits(
  env: Env,
  sub: string,
  day: string,
  credits: DailyFreeCredits,
): Promise<void> {
  const key = `credits:daily:${sub}:${day}`;
  await env.CARRIER_USERS.put(key, JSON.stringify(credits), {
    expirationTtl: 2 * 24 * 60 * 60, // 2 days
  });
}

async function getCreditConfig(
  env: Env,
  sub: string,
  tier: Tier,
): Promise<CreditConfig> {
  const key = `credits:config:${sub}`;
  const raw = await env.CARRIER_USERS.get(key, "json").catch(() => null);
  if (raw && typeof raw === "object" && "billing_threshold_cents" in raw) {
    return raw as CreditConfig;
  }
  // Default config
  return {
    billing_threshold_cents: DEFAULT_BILLING_THRESHOLD_CENTS,
    overages_enabled: tier !== "free",
    rollovers_enabled: tier !== "free",
    overage_rate_cents: OVERAGE_PRICE_PER_CREDIT_CENTS,
    volume_discount_pct: 0,
    notify_at_pct: [50, 80, 95, 100],
    updated_at: new Date().toISOString(),
  };
}

// ---------------------------------------------------------------------------
// Admin: Update Credit Config
// ---------------------------------------------------------------------------

/**
 * Update a user's credit configuration (admin or self-service).
 */
export async function updateCreditConfig(
  env: Env,
  sub: string,
  updates: Partial<CreditConfig>,
): Promise<CreditConfig> {
  const tier = await resolveUserTier(env, sub);
  const current = await getCreditConfig(env, sub, tier);
  const updated: CreditConfig = {
    ...current,
    ...updates,
    updated_at: new Date().toISOString(),
  };
  const key = `credits:config:${sub}`;
  await env.CARRIER_USERS.put(key, JSON.stringify(updated));
  return updated;
}

/**
 * Get credit usage summary for a user (used by billing UI).
 */
export async function getCreditSummary(
  env: Env,
  sub: string,
  tier: Tier,
): Promise<{
  ledger: CreditLedger;
  daily_free: DailyFreeCredits;
  config: CreditConfig;
  rollover: RolloverBalance | null;
  volume_discount_pct: number;
}> {
  const month = currentMonth();
  const today = currentDay();
  const ledger = await getLedger(env, sub, month, tier);
  const dailyFree = await getDailyFreeCredits(env, sub, today);
  const config = await getCreditConfig(env, sub, tier);
  const rolloverRaw = await env.CARRIER_USERS.get(
    `credits:rollover:${sub}`,
    "json",
  ).catch(() => null);
  const rollover = rolloverRaw as RolloverBalance | null;
  const volumeDiscount = resolveVolumeDiscount(ledger.consumed);

  return { ledger, daily_free: dailyFree, config, rollover, volume_discount_pct: volumeDiscount };
}

// ---------------------------------------------------------------------------
// Stripe Credit Grants
// ---------------------------------------------------------------------------

/**
 * Issue a credit grant to a customer via Stripe Customer Balance Transactions.
 * Used for promotional credits, referral bonuses, and compensation.
 */
export async function issueStripeCredits(
  env: Env,
  sub: string,
  amountCents: number,
  description: string,
): Promise<boolean> {
  const stripeKey = (env as Env & { STRIPE_SECRET_KEY?: string }).STRIPE_SECRET_KEY;
  if (!stripeKey) return false;

  const customerId = await env.CARRIER_USERS.get(`stripe_customer_id:${sub}`);
  if (!customerId) return false;

  const body = new URLSearchParams({
    amount: String(-Math.abs(amountCents)), // Negative = credit to customer
    currency: "usd",
    description,
  });

  const resp = await fetch(
    `https://api.stripe.com/v1/customers/${customerId}/balance_transactions`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${stripeKey}`,
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: body.toString(),
    },
  );

  return resp.ok;
}

// ---------------------------------------------------------------------------
// Internal Helpers
// ---------------------------------------------------------------------------

async function resolveUserTier(env: Env, sub: string): Promise<Tier> {
  const raw = await env.CARRIER_USERS.get(`user:${sub}`, "json").catch(() => null);
  if (raw && typeof raw === "object" && "tier" in raw) {
    const t = (raw as { tier: string }).tier;
    if (t === "pro" || t === "enterprise" || t === "superadmin") return t;
  }
  return "free";
}

function currentMonth(): string {
  const now = new Date();
  return `${now.getUTCFullYear()}${String(now.getUTCMonth() + 1).padStart(2, "0")}`;
}

function currentDay(): string {
  const now = new Date();
  return `${now.getUTCFullYear()}${String(now.getUTCMonth() + 1).padStart(2, "0")}${String(now.getUTCDate()).padStart(2, "0")}`;
}

function previousMonth(): string {
  const now = new Date();
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
  return `${d.getUTCFullYear()}${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

function firstDayNextMonth(): string {
  const now = new Date();
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth() + 1;
  if (m === 12) return new Date(Date.UTC(y + 1, 0, 1)).toISOString();
  return new Date(Date.UTC(y, m, 1)).toISOString();
}

function firstDayMonthAfterNext(): string {
  const now = new Date();
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth() + 2;
  if (m >= 12) return new Date(Date.UTC(y + 1, m - 12, 1)).toISOString();
  return new Date(Date.UTC(y, m, 1)).toISOString();
}
